import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { followService, FollowUser } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { X, User as UserIcon, Users, TrendingUp } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { enGB } from "date-fns/locale";
import toast from "react-hot-toast";

interface FollowersModalProps {
  isOpen: boolean;
  onClose: () => void;
  username: string;
  type: "followers" | "following";
}

const FollowersModal: React.FC<FollowersModalProps> = ({ isOpen, onClose, username, type }) => {
  const { user } = useAuth();
  const [users, setUsers] = useState<FollowUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [unfollowingUsers, setUnfollowingUsers] = useState<Set<string>>(new Set());

  const limit = 20;
  const isOwnProfile = user?.username === username;
  const title = type === "followers" ? "Followers" : "Following"; // ✅ Translated

  useEffect(() => {
    if (isOpen) {
      loadUsers(0);
    } else {
      // Reset state when modal closes
      setUsers([]);
      setSkip(0);
      setTotal(0);
      setHasMore(true);
    }
  }, [isOpen, username, type]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadUsers = async (currentSkip: number) => {
    try {
      setLoading(true);

      let newUsers: FollowUser[];
      let responseTotal: number;

      if (type === "followers") {
        const response = await followService.getFollowers(username, currentSkip, limit);
        newUsers = response.followers;
        responseTotal = response.total;
      } else {
        const response = await followService.getFollowing(username, currentSkip, limit);
        newUsers = response.following;
        responseTotal = response.total;
      }

      if (currentSkip === 0) {
        setUsers(newUsers);
      } else {
        setUsers((prev) => [...prev, ...newUsers]);
      }

      setTotal(responseTotal);
      setSkip(currentSkip + limit);
      setHasMore(newUsers.length === limit);
    } catch (error) {
      toast.error("Error loading users"); // ✅ Translated
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      loadUsers(skip);
    }
  };

  const handleUnfollow = async (userId: string, targetUsername: string) => {
    if (!isOwnProfile || type !== "following") return;

    try {
      setUnfollowingUsers((prev) => new Set(prev).add(userId));

      await followService.unfollowUser(userId);

      // Remove user from list
      setUsers((prev) => prev.filter((u) => u.id !== userId));
      setTotal((prev) => prev - 1);

      toast.success(`Unfollowed ${targetUsername}`); // ✅ Translated
    } catch (error: any) {
      toast.error(error.response?.data?.detail || "Error unfollowing user"); // ✅ Translated
    } finally {
      setUnfollowingUsers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-md max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-newspaper-300">
          <h2 className="text-xl font-semibold text-newspaper-900">
            {title} ({total})
          </h2>
          <button onClick={onClose} className="text-newspaper-500 hover:text-newspaper-700 p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading && users.length === 0 ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center space-x-3">
                  <div className="beat-loading w-12 h-12 rounded-full"></div>
                  <div className="flex-1">
                    <div className="beat-loading h-4 mb-2"></div>
                    <div className="beat-loading h-3 w-3/4"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 mx-auto mb-3 text-newspaper-400" />
              <p className="text-newspaper-600">
                {type === "followers" ? "No followers yet" : "Not following anyone yet"} {/* ✅ Translated */}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {users.map((followUser) => (
                <div
                  key={followUser.id}
                  className="flex items-center space-x-3 p-3 rounded-lg hover:bg-newspaper-50 transition-colors"
                >
                  {/* Avatar */}
                  <Link to={`/voice/${followUser.username}`} onClick={onClose}>
                    <div className="w-12 h-12 bg-newspaper-200 rounded-full flex items-center justify-center flex-shrink-0">
                      {followUser.avatar ? (
                        <img
                          src={followUser.avatar}
                          alt={followUser.username}
                          className="w-full h-full rounded-full object-cover"
                        />
                      ) : (
                        <UserIcon className="w-6 h-6 text-newspaper-600" />
                      )}
                    </div>
                  </Link>

                  {/* User Info */}
                  <div className="flex-1 min-w-0">
                    <Link to={`/voice/${followUser.username}`} onClick={onClose}>
                      <h4 className="font-medium text-newspaper-900 hover:text-newspaper-700 transition-colors">
                        {followUser.username}
                      </h4>
                    </Link>

                    {followUser.bio && <p className="text-sm text-newspaper-600 truncate">{followUser.bio}</p>}

                    <div className="flex items-center space-x-3 text-xs text-newspaper-500 mt-1">
                      <span className="flex items-center">
                        <TrendingUp className="w-3 h-3 mr-1" />
                        {Math.round(followUser.mojo)}
                      </span>
                      <span>{followUser.followers_count} followers</span> {/* ✅ Translated */}
                      <span>
                        {formatDistanceToNow(new Date(followUser.created_at), {
                          addSuffix: false,
                          locale: enGB,
                        })}
                      </span>
                    </div>
                  </div>

                  {/* Unfollow Button */}
                  {isOwnProfile && type === "following" && (
                    <button
                      onClick={() => handleUnfollow(followUser.id, followUser.username)}
                      disabled={unfollowingUsers.has(followUser.id)}
                      className="px-3 py-1 text-xs bg-newspaper-200 text-newspaper-700 rounded-full hover:bg-newspaper-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {unfollowingUsers.has(followUser.id) ? "Unfollowing..." : "Unfollow"} {/* ✅ Translated */}
                    </button>
                  )}
                </div>
              ))}

              {/* Load More Button */}
              {hasMore && (
                <div className="text-center py-4">
                  <button onClick={handleLoadMore} disabled={loading} className="beat-button-secondary">
                    {loading ? "Loading..." : "Load more"} {/* ✅ Translated */}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FollowersModal;
